// ===========================================================================
// Is the other person's camera really on?
//
// The 1:1 chat gives the window to the video when a camera is on and to the
// text when none is (the operator, 2026-09-15: "only when someone has a
// camera on is the text chat window small"). Having a video TRACK is not the
// same as having a picture. The camera button disables the track rather than
// removing it, and a disabled track keeps arriving on the other side as black
// frames with nothing on the track to say so. The only honest test is to look.
//
// So the other person's video is sampled on a 32x32 copy once a second, and a
// frame whose brightest pixel is near black counts as no picture. A dark room
// is not black: a real camera always has something above that floor, so the
// video is not taken away from somebody sitting in low light. Showing the
// video takes one live sample; taking it away takes two dark ones in a row, so
// one bad frame does not flip the layout. A probe that cannot read the frame
// answers "live": failing to measure must never hide a real picture.
//
// Pure functions plus one small timer, so the decisions can be tested without
// a page. ASCII only.
// ===========================================================================

/** At or above this, a pixel is something a camera saw. Black frames are 0. */
export const BLACK_LUMA = 16;
/** The size of the copy a frame is sampled at. */
export const SAMPLE_SIZE = 32;

/** The brightest pixel's luma (0..255) in an RGBA buffer. */
export function brightestLuma(rgba) {
  let max = 0;
  for (let i = 0; i + 2 < rgba.length; i += 4) {
    const luma = 0.299 * rgba[i] + 0.587 * rgba[i + 1] + 0.114 * rgba[i + 2];
    if (luma > max) max = luma;
  }
  return max;
}

/** A frame with no picture in it: empty, or nothing brighter than near black. */
export function isBlackFrame(rgba) {
  if (!rgba || rgba.length < 4) return true;
  return brightestLuma(rgba) < BLACK_LUMA;
}

/**
 * Turns a run of samples into a steady answer.
 *
 * @param {{ toLive?: number, toDark?: number }} [p] how many agreeing samples
 *   in a row flip the answer each way
 * @returns {{ push: (sampleLive: boolean) => boolean|null, readonly live: boolean }}
 *   push answers the new state when it changes, else null
 */
export function createLiveTracker({ toLive = 1, toDark = 2 } = {}) {
  let live = false;
  let streak = 0;
  return {
    push(sampleLive) {
      if (sampleLive === live) {
        streak = 0;
        return null;
      }
      streak += 1;
      if (streak >= (sampleLive ? toLive : toDark)) {
        live = sampleLive;
        streak = 0;
        return live;
      }
      return null;
    },
    get live() {
      return live;
    },
  };
}

/**
 * Whether this video element is showing a picture right now.
 *
 * @param {HTMLVideoElement} video
 * @param {CanvasRenderingContext2D|null} ctx a SAMPLE_SIZE square to draw into
 */
export function frameIsLive(video, ctx) {
  const stream = video && video.srcObject;
  const track = stream && typeof stream.getVideoTracks === 'function' ? stream.getVideoTracks()[0] : null;
  if (!track || track.readyState !== 'live' || track.muted) return false;
  if (!video.videoWidth || !video.videoHeight) return false;
  try {
    ctx.drawImage(video, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
    return !isBlackFrame(ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data);
  } catch {
    return true; // could not look, so do not hide what may be a real picture
  }
}

/**
 * Watch a video element and say when its picture comes and goes.
 *
 * @param {{ video: HTMLVideoElement, onChange: (live: boolean) => void,
 *           intervalMs?: number, doc?: Document }} p
 * @returns {{ stop: () => void, readonly live: boolean }}
 */
export function createRemoteVideoWatch({ video, onChange, intervalMs = 1000, doc = globalThis.document }) {
  let ctx = null;
  try {
    const canvas = doc.createElement('canvas');
    canvas.width = SAMPLE_SIZE;
    canvas.height = SAMPLE_SIZE;
    ctx = canvas.getContext('2d', { willReadFrequently: true });
  } catch {
    ctx = null; // frameIsLive answers live when it cannot draw
  }
  const tracker = createLiveTracker();
  const tick = () => {
    const next = tracker.push(frameIsLive(video, ctx));
    if (next !== null) onChange(next);
  };
  const timer = setInterval(tick, intervalMs);
  tick();
  return {
    stop() {
      clearInterval(timer);
    },
    get live() {
      return tracker.live;
    },
  };
}

export default { BLACK_LUMA, SAMPLE_SIZE, brightestLuma, isBlackFrame, createLiveTracker, frameIsLive, createRemoteVideoWatch };
